import { Erc20Abi, PoolAbi } from '@shared/Abis'
import { PoolContractCTO } from '@shared/ContractCTO'
import { DynamicDepositStatusEnum } from '@shared/Enums'
import { LoanCTO } from '@shared/LoanCTO'
import { logOT } from '@shared/loggingShared'
import { PoolCrossChainTransferCTO, TransferStepToString } from '@shared/PoolCrossChainDataCTO'

import { DynamicDepositActivityCTO } from '@shared/PoolEventCTO'
import { PoolOffchainDataCTO, PoolOffchainDataSchema } from '@shared/PoolOffchainDataCTO'
import { PoolAccountCTOProperties, PoolFlexAccountCTOProperties } from '@shared/SchemaProperties'
import { ETH_ADDR_ZERO, EthAddr, PoolAccountAccessPair, ProviderReadOnly, TxHash } from '@shared/SchemaTypes'
import {
  DynamicWithdrawalActivityCTO,
  PoolAccountCTO,
  PoolAccountSummaryCTO,
  ContractCTO,
  PoolDynamicAccountCTO,
  PoolDynamicOverviewCTO,
  PoolEventCTO,
  PoolFlexAccountCTO,
  PoolFlexAccountSummaryCTO,
  PoolFlexOverviewCTO,
  PoolOverviewCTO,
  PoolFlexAccountSummaryCTOSchema,
  PoolTermAccountSummaryCTOSchema,
} from '@shared/sharedTypes'
import { EventNameForTemplate, EventTypeForTemplate, WithdrawEventTypes } from '@shared/templateApx'
import axios from 'axios'
import { Contract, ethers } from 'ethers'
import { User } from 'oidc-client-ts'

//
export const fetchBalance = async (
  vaultAddr: EthAddr,
  liquidityAssetAddr: EthAddr,
  providerReadOnly: ProviderReadOnly
): Promise<bigint> => {
  const contract = new ethers.Contract(liquidityAssetAddr, Erc20Abi, providerReadOnly)
  const balance = await contract.balanceOf(vaultAddr)

  return BigInt(balance)
}

export const fetchPoolDynamicDeposits = async (
  poolDynamicOverviewCTO: PoolDynamicOverviewCTO,
  serverApiBaseUrl: string,
  restAPIKey: string,
  numDays = 30
): Promise<DynamicDepositActivityCTO[]> => {
  const thirtyDaysAgo = new Date()
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - numDays)
  const thirtyDaysAgoStr = thirtyDaysAgo
    ? `${thirtyDaysAgo.getFullYear()}-${String(thirtyDaysAgo.getMonth() + 1).padStart(2, '0')}-${String(
        thirtyDaysAgo.getDate()
      ).padStart(2, '0')}`
    : ''
  // const endDayStr = endDate
  //   ? `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`
  //   : ''

  const poolAddr = poolDynamicOverviewCTO.poolAddr
  const _poolEventDTOs = await fetchPoolEventCTOs(
    serverApiBaseUrl,
    restAPIKey,
    poolAddr,
    undefined,
    thirtyDaysAgoStr,
    undefined
  )

  logOT.debug('fetchPoolDynamicDeposits_poolEventDTOs ', _poolEventDTOs)
  const deposits: DynamicDepositActivityCTO[] = []

  _poolEventDTOs.forEach((event) => {
    const lender = event.senderAddr ?? event.accountAddr
    logOT.debug('event', event.eventName)
    if (event.eventType === EventTypeForTemplate.PoolDeposit) {
      logOT.debug('PoolDeposit ', event)
      const offChainTransferMatch = _poolEventDTOs.find(
        (e) => event.transferTxHash === e.transactionHash && e.eventType === EventTypeForTemplate.Transfer
      )
      const deposit: DynamicDepositActivityCTO = {
        poolAddr: event.poolAddr,
        lender: event.accountAddr,
        assets: event.assets,
        shares: event.shares,
        fundsOrigination: event.senderAddr ?? event.accountAddr,
        txHash: event.transactionHash as TxHash,
        state: offChainTransferMatch ? DynamicDepositStatusEnum.TRANSFER_ACCEPTED : DynamicDepositStatusEnum.CONTRACT,
      }
      if (event.depositType === 2) {
        deposit.state = DynamicDepositStatusEnum.OFFCHAIN
      }

      deposits.push(deposit)
    }
    if (event.eventType === EventTypeForTemplate.AccountCredit) {
      logOT.debug('PoolDeposit ', event)

      const deposit: DynamicDepositActivityCTO = {
        poolAddr: event.poolAddr,
        lender: event.accountAddr,
        assets: event.assets,
        shares: event.shares,
        fundsOrigination: event.senderAddr ?? event.accountAddr,
        txHash: event.transactionHash as TxHash,
        state: DynamicDepositStatusEnum.ACCOUNT_CREDIT,
      }

      deposits.push(deposit)
    }
    if (event.eventType === EventTypeForTemplate.Transfer) {
      if (event.assets == '0') {
        logOT.debug('asset == 0 , skip')
        return
      }
      const depositMatch = _poolEventDTOs.find(
        (e) => e.transactionHash === event.transactionHash && e.eventType === EventTypeForTemplate.PoolDeposit
      )
      const adjustmentMatch = _poolEventDTOs.find(
        (e) => e.transactionHash === event.transactionHash && e.eventType === EventTypeForTemplate.AccountCredit
      )
      const offChainDepositMatch = _poolEventDTOs.find(
        (e) => e.transferTxHash === event.transactionHash && e.eventType === EventTypeForTemplate.PoolDeposit
      )
      const nonLenderMatch =
        poolDynamicOverviewCTO.poolAdminAddr == lender ||
        poolDynamicOverviewCTO.borrowerManagerAddr == lender ||
        poolDynamicOverviewCTO.borrowerWalletAddr == lender

      logOT.debug('TRANSFER ', event, depositMatch)

      if (depositMatch || adjustmentMatch || nonLenderMatch) {
        // dedup
      } else if (offChainDepositMatch) {
        // dedup‰
      } else {
        const deposit: DynamicDepositActivityCTO = {
          poolAddr: event.poolAddr,
          lender: event.senderAddr ?? event.accountAddr,
          assets: event.assets,
          shares: event.shares,
          fundsOrigination: event.senderAddr ?? event.accountAddr,
          txHash: event.transactionHash as TxHash,
          state: DynamicDepositStatusEnum.TRANSFER_PENDING,
        }
        deposits.push(deposit)
        logOT.debug('event', event)
      }
    }
  })

  return deposits
}

export const fetchParticipantsInPools = async (
  serverApiBaseUrl: string,
  restAPIKey: string,
  poolAddrs: EthAddr[]
): Promise<EthAddr[]> => {
  const participantSet = new Set<EthAddr>()

  const poolAccountAccessPairs = await fetchAllPoolAccountAccessPairs(serverApiBaseUrl, restAPIKey)
  poolAccountAccessPairs.forEach((pair) => {
    if (poolAddrs.includes(pair.poolAddr)) {
      participantSet.add(pair.accountAddr)
    }
  })
  logOT.debug('fetchParticipantsInPools', poolAddrs, participantSet)

  return Array.from(participantSet)
}
export async function fetchAllPoolAccountAccessPairsRegenerateForPool(
  serverApiBaseUrl: string,
  restAPIKey: string,
  poolAddr: EthAddr
): Promise<PoolAccountAccessPair[]> {
  const response = await axios.get(`${serverApiBaseUrl}/poolAccountPairs?poolAddr=${poolAddr}&regenerate=true`, {
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': restAPIKey,
    },
  })
  return response.data.result || []
}
export const fetchPoolParticipants = async (
  serverApiBaseUrl: string,
  restAPIKey: string,
  poolAddr: EthAddr
): Promise<PoolAccountAccessPair[]> => {
  logOT.info('fetchPoolParticipants ', poolAddr, serverApiBaseUrl, restAPIKey)
  const response = await axios.get(`${serverApiBaseUrl}/poolAccountPairs?poolAddr=${poolAddr}`, {
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': restAPIKey,
      Accept: 'application/json',
    },
  })
  logOT.info('fetchPoolParticipants response', response.data.result)
  return response.data.result || []
}
export async function fetchAllPoolAccountAccessPairs(
  serverApiBaseUrl: string,
  restAPIKey: string
): Promise<PoolAccountAccessPair[]> {
  const response = await axios.get(`${serverApiBaseUrl}/poolAccountPairs`, {
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': restAPIKey,
    },
  })
  return response.data.result || []
}

export async function fetchPoolAccountAccessPairRegenerationLocalHostOnly(
  serverApiBaseUrl: string,
  restAPIKey: string
): Promise<PoolAccountAccessPair[]> {
  const response = await axios.get(`${serverApiBaseUrl}/poolAccountPairs?regenerate=true`, {
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': restAPIKey,
    },
  })
  return response.data.result || []
}

export const fetchPoolAccountAccessPairs = async (
  serverApiBaseUrl: string,
  restAPIKey: string,
  accountAddr: EthAddr
): Promise<PoolAccountAccessPair[]> => {
  const response = await axios.get(`${serverApiBaseUrl}/poolAccountPairs?accountAddr=${accountAddr}`, {
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': restAPIKey,
      Accept: 'application/json',
    },
  })
  return response.data.result || []
}

// NOTE: This is very similar to fetchPoolAccountCTO. But this one will fetch every pool account for the given account address.
// This suppose to be an internal usage api endpoint.
export const fetchPoolAccountsList = async (
  serverApiBaseUrl: string,
  restAPIKey: string,
  accountAddr: EthAddr
): Promise<(PoolAccountCTO | PoolFlexAccountCTO | PoolDynamicAccountCTO)[]> => {
  const response = await axios.get(`${serverApiBaseUrl}/poolAccounts/${accountAddr}`, {
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': restAPIKey,
      Accept: 'application/json',
    },
  })
  return response.data || []
}

export const fetchPoolAccountCTO = async (
  serverApiBaseUrl: string,
  restAPIKey: string,
  poolAddr: EthAddr,
  lenderAddr: EthAddr,
  blockNumber?: number
): Promise<PoolAccountCTO | PoolFlexAccountCTO | undefined> => {
  const url = blockNumber
    ? `${serverApiBaseUrl}/vaultsAccount/${poolAddr}/${lenderAddr}?blockNumber=${blockNumber}`
    : `${serverApiBaseUrl}/vaultsAccount/${poolAddr}/${lenderAddr}`
  const response = await axios.get(url, {
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': restAPIKey,
      Accept: 'application/json',
    },
  })

  logOT.debug('fetchPoolAccountCTO response', response.data.vaultAccountCTO)
  return response.data.vaultAccountCTO
}

export const fetchPoolOverviewCTO = async (
  serverApiBaseUrl: string,
  restAPIKey: string,
  poolAddr: EthAddr
): Promise<PoolOverviewCTO | PoolFlexOverviewCTO | PoolDynamicOverviewCTO | undefined> => {
  const response = await axios.get(`${serverApiBaseUrl}/vaults/${poolAddr}`, {
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': restAPIKey,
    },
  })
  logOT.info('fetchPoolOverviewCTO response', response.data.vaultOverviewCTO)
  return response.data.vaultOverviewCTO
}
export async function fetchPoolFlexAccountHistory(
  graphqlUrl: string,
  restAPIKey: string,
  poolAddr: EthAddr,
  lenderAddr: EthAddr,
  startDate: Date,
  endDate: Date
): Promise<{ poolAccountCTOs: PoolFlexAccountCTO[]; poolAccountSummary: PoolFlexAccountSummaryCTO }> {
  try {
    const propertyListString = PoolFlexAccountCTOProperties.join('\n')
    const query = `query GetPoolFlexAccountsForDateRange($poolAddr: String!, $accountAddr: String!, $startDayStr: String!, $endDayStr: String!) {
    getPoolFlexAccountsForDateRange(
        poolAddr: $poolAddr
        accountAddr: $accountAddr
        startDayStr: $startDayStr
        endDayStr: $endDayStr
    ) {
          ${propertyListString}
          }
  getPoolFlexAccountSummaryForDateRange(
        poolAddr: $poolAddr
        accountAddr: $accountAddr
        startDayStr: $startDayStr
        endDayStr: $endDayStr
    ) {
        startBlockNumber
        startTimestamp
        endBlockNumber
        endTimestamp
        averageTokenBalance
        sumInterestInADay
        startDayNumber
        endDayNumber

    }
  }`
    const startDayStr = `${startDate.getFullYear()}-${startDate.getMonth() + 1}-${startDate.getDate()}`
    const endDayStr = `${endDate.getFullYear()}-${endDate.getMonth() + 1}-${endDate.getDate()}`

    const variables = {
      poolAddr: poolAddr,
      accountAddr: lenderAddr,
      startDayStr,
      endDayStr,
    }

    const response = await axios.post(
      `${graphqlUrl}/graphql`,
      {
        query: query,
        variables: variables,
      },
      {
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': restAPIKey,
        },
      }
    )

    return {
      poolAccountCTOs: response.data.data.getPoolFlexAccountsForDateRange,
      poolAccountSummary: response.data.data.getPoolFlexAccountSummaryForDateRange,
    }
  } catch (e) {
    logOT.error('fetchPoolFlexAccountHistory error', e)
  }
  return { poolAccountCTOs: [], poolAccountSummary: PoolFlexAccountSummaryCTOSchema.cast({}) }
}

export async function fetchPoolTermAccountHistory(
  graphqlUrl: string,
  restAPIKey: string,
  poolAddr: EthAddr,
  accountAddr: EthAddr,
  startDate: Date,
  endDate: Date
): Promise<{ poolAccountCTOs: PoolAccountCTO[]; poolAccountSummary: PoolAccountSummaryCTO }> {
  try {
    const propertyListString = PoolAccountCTOProperties.join('\n')
    const query = `query GetPoolAccountsForDateRange($poolAddr: String!, $accountAddr: String!, $startDayStr: String!, $endDayStr: String!) {
    getPoolTermAccountsForDateRange(
        poolAddr: $poolAddr
        accountAddr: $accountAddr
        startDayStr: $startDayStr
        endDayStr: $endDayStr
    ) {
           ${propertyListString}
       }
           }`
    //     const _querySummary = `query GetPoolAccountSummaryForDateRange($poolAddr: String!, $accountAddr: String!, $startDayStr: String!, $endDayStr: String!) {

    //             getPoolTermAccountSummaryForDateRange(
    //        poolAddr: $poolAddr
    //         accountAddr: $accountAddr
    //         startDayStr: $startDayStr
    //         endDayStr: $endDayStr
    //     ) {
    //          startBlockNumber
    //         startTimestamp
    //         endBlockNumber
    //         endTimestamp
    //         sumInterestInADay
    //         startDayNumber
    //         endDayNumber

    //     }
    // }`
    // const startDayStr = `${startDate.getFullYear()}-${startDate.getMonth() + 1}-${startDate.getDate()}`
    // const endDayStr = `${endDate.getFullYear()}-${endDate.getMonth() + 1}-${endDate.getDate()}`

    // const startDayStr = startDate.toISOString().split('T')[0]
    // const endDayStr = endDate.toISOString().split('T')[0]

    const startDayStr = `${startDate.getFullYear()}-${startDate.getMonth() + 1}-${startDate.getDate()}`
    const endDayStr = `${endDate.getFullYear()}-${endDate.getMonth() + 1}-${endDate.getDate()}`

    const variables = {
      poolAddr,
      accountAddr,
      startDayStr,
      endDayStr,
    }
    const response = await axios.post(
      `${graphqlUrl}/graphql`,
      {
        query: query,
        variables,
      },
      {
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': restAPIKey,
        },
      }
    )
    // const responseSummary = await axios.post(
    //   `${graphqlUrl}/graphql`,
    //   {
    //     query: querySummary,
    //     variables,
    //   },
    //   {
    //     headers: {
    //       'Content-Type': 'application/json',
    //       'x-api-key': restAPIKey,
    //     },
    //   }
    // )

    return {
      poolAccountCTOs: response.data.data.getPoolTermAccountsForDateRange,
      poolAccountSummary: PoolTermAccountSummaryCTOSchema.cast({}), //responseSummary.data.data.getPoolTermAccountSummaryForDateRange,
    }
  } catch (e) {
    logOT.error('fetchPoolTermAccountHistory error', e)
  }
  return { poolAccountCTOs: [], poolAccountSummary: PoolTermAccountSummaryCTOSchema.cast({}) }
}

export async function fetchDynamicPoolWithdrawActivitiesCTOs(
  serverApiBaseUrl: string,
  restAPIKey: string,
  poolAddr: EthAddr,
  accountAddr?: EthAddr
): Promise<DynamicWithdrawalActivityCTO[]> {
  logOT.info('fetchDynamicPoolWithdrawActivitiesCTOs ', serverApiBaseUrl, restAPIKey, poolAddr, accountAddr)
  const url = accountAddr
    ? `${serverApiBaseUrl}/dynamicWithdrawActivities/${poolAddr}/${accountAddr}`
    : `${serverApiBaseUrl}/dynamicWithdrawActivities/${poolAddr}`
  const response = await axios.get(url, {
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': restAPIKey,
    },
  })
  logOT.info('fetchDynamicPoolWithdrawActivitiesCTOs response', response.data)

  logOT.info('filtered fetchDynamicPoolWithdrawActivitiesCTOs response', response.data)
  return response.data.dynamicWithdrawActivities
}

export async function scanPoolsForEvents(
  serverApiBaseUrl: string,
  restAPIKey: string,
  poolAddr?: EthAddr
): Promise<void> {
  const url = poolAddr
    ? `${serverApiBaseUrl}/scanAllPoolsForEvents/${poolAddr}`
    : `${serverApiBaseUrl}/scanAllPoolsForEvents`
  logOT.debug('scanPoolsForEvents start', poolAddr)
  const response = await axios.get(url, {
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': restAPIKey,
    },
  })
  logOT.debug('scanPoolsForEvents end')
  logOT.debug('scanPoolsForEvents response', response)
}

export async function fetchPoolEventCTOs(
  serverApiBaseUrl: string,
  restAPIKey: string,
  poolAddr: EthAddr,
  accountAddr?: EthAddr,
  startDayStr?: string,
  endDayStr?: string
): Promise<PoolEventCTO[]> {
  try {
    logOT.info('fetchPoolEventCTOs', serverApiBaseUrl, poolAddr, accountAddr)
    const url = accountAddr
      ? `${serverApiBaseUrl}/poolEvents/${poolAddr}/${accountAddr}?startDayStr=${startDayStr ?? ''}&endDayStr=${
          endDayStr ?? ''
        }`
      : `${serverApiBaseUrl}/poolEvents/${poolAddr}?startDayStr=${startDayStr ?? ''}&endDayStr=${endDayStr ?? ''}`

    const response = await axios.get(url, {
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': restAPIKey,
      },
    })

    const usedTXs: string[] = []

    const ret = response.data.poolEvents
      .filter((ev: PoolEventCTO) => ev && ev.transactionHash && ev.blockNumber)
      .sort((a: PoolEventCTO, b: PoolEventCTO) => (b?.blockNumber ?? 0) - (a?.blockNumber ?? 0))
      .filter((ev: PoolEventCTO) => {
        const dedup = ev.id

        if (usedTXs.includes(dedup)) return false
        usedTXs.push(dedup)
        return true
      })
    logOT.info(' ret', ret)
    return ret
  } catch (e) {
    logOT.error('fetchPoolEventCTOs error', e)
  }

  return []
}

export async function fetchForAccountAddrPoolEventCTOs(
  serverApiBaseUrl: string,
  restAPIKey: string,
  accountAddr: EthAddr,
  startDayStr?: string,
  endDayStr?: string
): Promise<PoolEventCTO[]> {
  try {
    const poolAddr = ETH_ADDR_ZERO
    const url = `${serverApiBaseUrl}/poolEvents/${poolAddr}/${accountAddr}?startDayStr=${startDayStr ?? ''}&endDayStr=${
      endDayStr ?? ''
    }`

    const response = await axios.get(url, {
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': restAPIKey,
      },
    })

    const usedTXs: string[] = []

    const ret = response.data.poolEvents
      .filter((ev: PoolEventCTO) => ev && ev.transactionHash && ev.blockNumber)
      .sort((a: PoolEventCTO, b: PoolEventCTO) => (b?.blockNumber ?? 0) - (a?.blockNumber ?? 0))
      .filter((ev: PoolEventCTO) => {
        const dedup = ev.id

        if (usedTXs.includes(dedup)) return false
        usedTXs.push(dedup)
        return true
      })
    logOT.info(' fetchForAccountAddrPoolEventCTOs ret', accountAddr, ret)
    return ret
  } catch (e) {
    logOT.error('fetchPoolEventCTOs error', e)
  }

  return []
}
export const fetchPoolTermEarlyWithdrawEventCTOs = async (
  serverApiBaseUrl: string,
  restAPIKey: string,
  poolAddr: EthAddr,
  loanAddr: EthAddr | null,
  lenderAddr: EthAddr | null
): Promise<PoolEventCTO[]> => {
  try {
    const allPoolEvents = await fetchPoolEventCTOs(serverApiBaseUrl, restAPIKey, poolAddr)

    const events = allPoolEvents.filter((e) => {
      if (
        e.eventType === EventTypeForTemplate.WithdrawEarlyRequested ||
        e.eventType === EventTypeForTemplate.WithdrawEarlyAccepted ||
        e.eventType === EventTypeForTemplate.WithdrawEarlyRepay
      ) {
        if (lenderAddr && loanAddr) {
          return e.accountAddr === lenderAddr && e.loanAddr === loanAddr
        } else if (lenderAddr && !loanAddr) {
          return e.accountAddr === lenderAddr
        }
        if (!lenderAddr && loanAddr) {
          return e.loanAddr === loanAddr
        } else {
          return true
        }
      }
      return false
    })

    return events
  } catch (e) {
    logOT.error('fetchPoolTermEarlyWithdrawEventCTOs error', e)
    return []
  }
}

export const fetchPoolFlexWithdrawActionPoolEventsCTOs = async (
  serverApiBaseUrl: string,
  restAPIKey: string,
  poolAddr: EthAddr,
  lenderAddr?: EthAddr
): Promise<PoolEventCTO[]> => {
  const events: PoolEventCTO[] = await fetchPoolEventCTOs(serverApiBaseUrl, restAPIKey, poolAddr, lenderAddr)
  const withdrawEvents: PoolEventCTO[] = events.filter((event: PoolEventCTO) => {
    return WithdrawEventTypes.includes(event.eventType)
  })

  const completeSequenceIds: string[] = []
  withdrawEvents.forEach((event: PoolEventCTO) => {
    if (event.eventName === EventNameForTemplate.RepayLoanToLender) {
      completeSequenceIds.push(event.withdrawSequenceId ?? '---')
    }
  })
  const incompleteWithdrawEvents = withdrawEvents.filter((event: PoolEventCTO) => {
    return !completeSequenceIds.includes(event.withdrawSequenceId ?? '')
  })

  return incompleteWithdrawEvents
}

export const fetchErc20BalanceForPool = async (
  providerReadOnly: ProviderReadOnly | undefined,
  accountAddr: EthAddr,
  poolAddr: EthAddr
): Promise<bigint> => {
  if (!providerReadOnly) {
    return 0n
  }
  const poolContract = new Contract(poolAddr, PoolAbi, providerReadOnly)
  const liquidityAsset = await poolContract.liquidityAssetAddr()
  const erc20Contract = new Contract(liquidityAsset, Erc20Abi, providerReadOnly)

  const b: bigint = await erc20Contract.balanceOf(accountAddr)

  return b
}

export const fetchLoanCTO = async (
  serverApiBaseUrl: string,
  restAPIKey: string,
  loanAddr: EthAddr
): Promise<LoanCTO | undefined> => {
  logOT.info('fetchLoanCTO', serverApiBaseUrl, restAPIKey, loanAddr)
  const response = await axios.get(`${serverApiBaseUrl}/loans/${loanAddr}`, {
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': restAPIKey,
    },
  })
  logOT.info('fetchLoanCTO response', response)
  return response.data.loanCTO
}

export const poolOffchainDataPut = async (
  serverApiBaseUrl: string,
  restAPIKey: string,
  poolOffchainDataCTO: PoolOffchainDataCTO,
  cognitoUser: User
): Promise<PoolOffchainDataCTO> => {
  const accessToken = cognitoUser?.access_token
  const headers = {
    Authorization: `Bearer ${accessToken}`,
    'x-api-key': restAPIKey,
    'Content-Type': 'application/json', // Assuming you're sending JSON data
  }

  logOT.info('poolOffchainDataPut', serverApiBaseUrl, headers)
  const r = await axios.put(`${serverApiBaseUrl}/poolOffChainData`, poolOffchainDataCTO, { headers })
  const data = r.data

  return data.results
}

export const poolOffchainDataFetch = async (
  serverApiBaseUrl: string,
  restAPIKey: string,
  poolAddr: EthAddr
): Promise<PoolOffchainDataCTO> => {
  const r = await axios.get(`${serverApiBaseUrl}/poolOffChainData/${poolAddr}`, {
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': restAPIKey,
    },
  })

  const data = r.data
  if (!data.result) {
    return PoolOffchainDataSchema.cast({
      poolAddrOffchain: poolAddr,
      benchmarkRate: '',
      timestampOffchain: 0,
      collateral: [],
    })
  }

  const poolCrossChainTransfer = data.result
  return poolCrossChainTransfer
}

export const poolCrossChainTransferFetch = async (
  serverApiBaseUrl: string,
  restAPIKey: string,
  poolAddr: EthAddr
): Promise<PoolCrossChainTransferCTO | undefined> => {
  try {
    const r = await axios.get(`${serverApiBaseUrl}/poolCrossChainTransfer/${poolAddr}`, {
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': restAPIKey,
      },
    })
    const data = r.data
    if (!data.result) {
      return undefined
    }

    const poolCrossChainTransferCTO = data.result
    return poolCrossChainTransferCTO
  } catch (e) {
    logOT.error('poolCrossChainTransferFetch error', e)
    return undefined
  }
}

export const fetchPools = async (
  serverApiBaseUrl: string,
  restAPIKey: string,
  chainConfigurationName: string
): Promise<ContractCTO[]> => {
  logOT.debug('#fetchPools  caps', `--${serverApiBaseUrl}--`, `--${restAPIKey}--`)
  try {
    const r = await axios.get(`${serverApiBaseUrl}/vaults`, {
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': restAPIKey,
      },
    })

    const data = r.data
    if (!data.vaultList) {
      return []
    }
    logOT.debug('#fetchPools  got vaults', data)
    const filteredList = data.vaultList.filter((vault: PoolContractCTO) => {
      vault.chainConfigName === chainConfigurationName
    })
    return filteredList
  } catch (e) {
    logOT.error('fetchPools error', e)
    return []
  }
}

export const poolCrossChainTransferPut = async (
  sourceServerApiBaseUrl: string,
  sourceRestAPIKey: string,
  destinationServerApiBaseUrl: string,
  destRestAPIKey: string,
  poolCrossChainTransferCTO: PoolCrossChainTransferCTO
): Promise<void> => {
  logOT.debug('#poolCrossChainTransferPut', sourceServerApiBaseUrl, 'dest', destinationServerApiBaseUrl)
  if (poolCrossChainTransferCTO.transferStepName != TransferStepToString[poolCrossChainTransferCTO.transferStep]) {
    throw new Error('transferStepName does not match transferStep')
  }

  logOT.debug('#poolCrossChainTransferPut poolCrossChainTransferCTO', poolCrossChainTransferCTO)
  const sourceTransferCTO = { ...poolCrossChainTransferCTO }
  const destinationTransferCTO = { ...poolCrossChainTransferCTO }
  sourceTransferCTO.poolAddr = sourceTransferCTO.sourcePoolAddr
  destinationTransferCTO.poolAddr = destinationTransferCTO.destinationPoolAddr

  logOT.debug('#poolCrossChainTransferPut', sourceTransferCTO, destinationTransferCTO)
  const r1 = await axios.put(
    `${sourceServerApiBaseUrl}/poolCrossChainTransfer/${sourceTransferCTO.poolAddr}`,
    sourceTransferCTO,
    {
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': sourceRestAPIKey,
      },
    }
  )
  const r2 = await axios.put(
    `${destinationServerApiBaseUrl}/poolCrossChainTransfer/${destinationTransferCTO.poolAddr}`,
    destinationTransferCTO,
    {
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': destRestAPIKey,
      },
    }
  )
  logOT.debug('#poolCrossChainTransferPut', r1.status, r2.status)
}

export const fetchPoolOverViews = async (
  serverApiBaseUrl: string,
  restAPIKey: string
): Promise<Array<PoolOverviewCTO | PoolFlexOverviewCTO | PoolDynamicOverviewCTO>> => {
  const response = await axios.get(`${serverApiBaseUrl}/poolsOverview`, {
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': restAPIKey,
    },
  })
  return response.data.poolOverviews || []
}

export const fetchPoolOverviewsByChain = async (
  serverApiBaseUrl: string,
  restAPIKey: string,
  chainName: string
): Promise<Array<PoolOverviewCTO | PoolFlexOverviewCTO | PoolDynamicOverviewCTO>> => {
  logOT.info('fetchPoolOverviewsByChain', serverApiBaseUrl, restAPIKey, chainName)
  const response = await axios.get(`${serverApiBaseUrl}/poolsOverview?chainName=${chainName}`, {
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': restAPIKey,
    },
  })
  logOT.debug('fetchPoolOverviewsByChain response:', response.data)
  return response.data || []
}

export const fetchDailyPoolOverviewCTOs = async (
  serverApiBaseUrl: string,
  restAPIKey: string,
  poolAddr: EthAddr,
  startDayStr: string | undefined,
  endDayStr: string | undefined
): Promise<Array<PoolOverviewCTO | PoolFlexOverviewCTO | PoolDynamicOverviewCTO>> => {
  logOT.info(
    '#fetchDailyPoolOverViews  caps SEND API KEY',
    startDayStr,
    endDayStr,
    `--${serverApiBaseUrl}--`,
    `--${restAPIKey}--`
  )
  try {
    const params: Record<string, string> = {}

    if (startDayStr) {
      params.startDayStr = startDayStr
    }
    if (endDayStr) {
      params.endDayStr = endDayStr
    }
    logOT.debug('fetchDailyPoolOverViews params', params, startDayStr, endDayStr)
    const r = await axios.get(`${serverApiBaseUrl}/dailyVaults/${poolAddr}`, {
      params,
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': restAPIKey,
      },
    })

    const data = r.data
    if (!data.dailyVaultsOverviewCTOs) {
      return []
    }
    logOT.debug('#dailyVaultsOverviewCTOs  got vaults', data.dailyVaultsOverviewCTOs)

    return data.dailyVaultsOverviewCTOs
  } catch (e) {
    logOT.error('fetchPoolOverViews error', e)
    return []
  }
}

export const fetchDailyPoolAccountCTOs = async (
  serverApiBaseUrl: string,
  restAPIKey: string,
  poolAddr: EthAddr,
  lenderAddr: EthAddr,
  startDayStr: string | undefined,
  endDayStr: string | undefined
): Promise<Array<PoolAccountCTO | PoolFlexAccountCTO | PoolDynamicAccountCTO>> => {
  logOT.debug('#fetchDailyPoolAccountCTOs  ', poolAddr, lenderAddr, startDayStr, endDayStr)
  try {
    const params: Record<string, string> = {}

    if (startDayStr) {
      params.startDayStr = startDayStr
    }
    if (endDayStr) {
      params.endDayStr = endDayStr
    }

    const url = `${serverApiBaseUrl}/dailyAccounts/${poolAddr}/${lenderAddr}`
    logOT.info('#fetchDailyPoolAccountCTOs  url', url, params)
    const r = await axios.get(url, {
      params,
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': restAPIKey,
        Accept: 'application/json',
      },
    })

    const data = r.data
    if (!data.dailyVaultsAccountCTOs) {
      return []
    }
    logOT.debug('#fetchDailyPoolAccountCTOs  got vaults', data.dailyVaultsAccountCTOs)

    return data.dailyVaultsAccountCTOs
  } catch (e) {
    logOT.error('fetchDailyPoolAccountCTOs error', e)
    return []
  }
}
